import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import type { DeletionRequestFilters } from "../api/services.type.ts"
import { accountDeletionSliceName } from "./account-deletion.constants.ts"
import {
  approveDeletionRequestThunk,
  fetchDeletionRequests,
  fetchPendingDeletionCount,
  rejectDeletionRequestThunk,
  retryDeletionRequestThunk
} from "./account-deletion.thunks.ts"
import type { AccountDeletionState } from "./account-deletion.types.ts"

const initialState: AccountDeletionState = {
  requests: [],
  next: null,
  pagination: { count: 0, page: 1, page_size: SCROLL_PAGE_SIZE },
  filters: { status: "pending", page: 1, page_size: SCROLL_PAGE_SIZE },
  fetching: false,
  requestId: null,
  deciding: false,
  pendingCount: 0
}

const accountDeletionSlice = createSlice({
  name: accountDeletionSliceName,
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<DeletionRequestFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder
      // The three states of a scrolled list, spelled out here rather than
      // through `redux/shared/paged-list` because this slice keeps its rows and
      // its count in separate fields. Same rule.
      .addCase(fetchDeletionRequests.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId

        if (!action.meta?.arg?.isMore) {
          state.requests = []
          state.next = null
          state.retryNext = null
        }
      })
      .addCase(fetchDeletionRequests.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.requests = action.meta?.arg?.isMore
          ? [...state.requests, ...action.payload.results]
          : action.payload.results
        state.next = action.payload.next ?? null
        state.retryNext = null
        state.pagination.count = action.payload.count
        // The badge needs the true pending total, so only an unsearched pending
        // list may set it — and judged by the request's own filters, since
        // `state.filters` may have moved on while this fetch was in flight.
        const { status, q } = action.meta.arg
        if (status === "pending" && !q) {
          state.pendingCount = action.payload.count
        }
      })
      .addCase(fetchDeletionRequests.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null

        // Keep the pages already scrolled in, but stop offering the one that
        // failed — the scroll hook re-fires on every settle while `next` stands.
        // The url moves to `retryNext`, which the footer offers the user. A
        // retry is built from `retryNext`, so on a second failure `next` is
        // already null and the url has to be kept rather than re-read.
        if (action.meta.arg?.isMore)
          state.retryNext = state.next ?? state.retryNext ?? null
        state.next = null
        if (!action.meta?.arg?.isMore) state.requests = []
      })

      .addCase(approveDeletionRequestThunk.pending, (state) => {
        state.deciding = true
      })
      .addCase(approveDeletionRequestThunk.fulfilled, (state) => {
        state.deciding = false
      })
      .addCase(approveDeletionRequestThunk.rejected, (state) => {
        state.deciding = false
      })

      .addCase(rejectDeletionRequestThunk.pending, (state) => {
        state.deciding = true
      })
      .addCase(rejectDeletionRequestThunk.fulfilled, (state) => {
        state.deciding = false
      })
      .addCase(rejectDeletionRequestThunk.rejected, (state) => {
        state.deciding = false
      })

      .addCase(retryDeletionRequestThunk.pending, (state) => {
        state.deciding = true
      })
      .addCase(retryDeletionRequestThunk.fulfilled, (state) => {
        state.deciding = false
      })
      .addCase(retryDeletionRequestThunk.rejected, (state) => {
        state.deciding = false
      })

      .addCase(fetchPendingDeletionCount.fulfilled, (state, action) => {
        state.pendingCount = action.payload
      })

      .addCase(logout.fulfilled, () => initialState)
  }
})

export const { setFilters } = accountDeletionSlice.actions

export default accountDeletionSlice
